import { fromJS, is, List, Map, Set } from 'immutable'

export const getSnapshotCodeId = group => (
  group.get('codeId') !== undefined ? group.get('codeId') : group.get('code_id')
)

export const getSnapshotNewsIds = group => {
  const newsIds = group.get('newsIds')

  if (newsIds !== undefined) {
    return newsIds
  }

  return group.get('news_ids') || List()
}

const normalizeId = id => (id == null ? null : String(id))

const assertUnique = (ids, label) => {
  if (ids.size !== ids.toSet().size) {
    throw new TypeError(`Invalid media review snapshot: duplicate ${label}`)
  }
}

export const normalizeSnapshot = snapshot => {
  const normalized = fromJS(snapshot || [])
    .map(group => {
      const newsIds = fromJS(getSnapshotNewsIds(group) || []).map(normalizeId)

      assertUnique(newsIds, 'news id')

      return fromJS({
        codeId: normalizeId(getSnapshotCodeId(group)),
        newsIds
      })
    })
    .filter(group => !group.get('newsIds').isEmpty())

  assertUnique(normalized.map(group => group.get('codeId')), 'code id')

  return normalized
}

export const snapshotMembershipMatches = (left, right) => {
  const membership = snapshot => normalizeSnapshot(snapshot).reduce(
    (result, group) => result.set(
      group.get('codeId'),
      group.get('newsIds').toSet()
    ),
    Map()
  )

  return is(membership(left), membership(right))
}

const mainNewsIdsFromSnapshots = snapshots => List(snapshots)
  .flatMap(snapshot => normalizeSnapshot(snapshot).flatMap(group => group.get('newsIds')))
  .toSet()

const overlayOrder = (referenceIds, desiredIds, movableIds) => {
  let result = List()
  let remaining = desiredIds
  let insertionIndex = null

  referenceIds.forEach(id => {
    if (movableIds.has(id)) {
      if (!remaining.isEmpty()) {
        result = result.push(remaining.first())
        remaining = remaining.shift()
      }

      insertionIndex = result.size
    } else {
      result = result.push(id)
    }
  })

  insertionIndex = insertionIndex === null ? result.size : insertionIndex

  return result
    .slice(0, insertionIndex)
    .concat(remaining)
    .concat(result.slice(insertionIndex))
}

export const projectSortingToSnapshot = (sorting, referenceSnapshots) => {
  const reference = normalizeSnapshot(fromJS(referenceSnapshots).first() || [])
  const referenceByCode = reference.reduce(
    (groups, group) => groups.set(group.get('codeId'), group),
    Map()
  )
  const mainNewsIds = mainNewsIdsFromSnapshots([reference])
  const desiredGroups = normalizeSnapshot(sorting)
    .filter(group => referenceByCode.has(group.get('codeId')))
    .map(group => group.update('newsIds', newsIds => (
      newsIds.filter(newsId => mainNewsIds.has(newsId))
    )))
  const movableNewsIds = desiredGroups.flatMap(group => group.get('newsIds')).toSet()
  const mergedByCode = desiredGroups.reduce((groups, group) => {
    const codeId = group.get('codeId')
    const referenceNewsIds = referenceByCode.getIn([codeId, 'newsIds'], List())

    return groups.set(
      codeId,
      group.set('newsIds', overlayOrder(referenceNewsIds, group.get('newsIds'), movableNewsIds))
    )
  }, Map())
  const desiredCodeIds = desiredGroups.map(group => group.get('codeId'))
  const movableCodeIds = desiredCodeIds.toSet()
  const mergedCodeIds = overlayOrder(
    reference.map(group => group.get('codeId')),
    desiredCodeIds,
    movableCodeIds
  )

  return mergedCodeIds.map(codeId => mergedByCode.get(codeId, referenceByCode.get(codeId)))
}

export const removeNewsFromSnapshot = (snapshot, newsIds) => {
  const ids = fromJS(newsIds || []).map(normalizeId).toSet()

  return normalizeSnapshot(snapshot)
    .map(group => group.update('newsIds', groupNewsIds => (
      groupNewsIds.filter(id => !ids.has(id))
    )))
    .filter(group => !group.get('newsIds').isEmpty())
}

const membershipsByNewsId = snapshot => normalizeSnapshot(snapshot).reduce(
  (memberships, group) => group.get('newsIds').reduce(
    (result, newsId) => result.update(newsId, Set(), codeIds => codeIds.add(group.get('codeId'))),
    memberships
  ),
  Map()
)

const snapshotWithoutNews = (snapshot, newsIds) => {
  const ids = fromJS(newsIds || []).map(normalizeId).toSet()

  return normalizeSnapshot(snapshot)
    .map(group => group.update('newsIds', groupNewsIds => (
      groupNewsIds.filter(id => !ids.has(id))
    )))
    .filter(group => !group.get('newsIds').isEmpty())
}

export const snapshotMatchesRegroup = (savedSnapshot, loadedSnapshot, newsId, codeIds) => {
  const normalizedNewsId = normalizeId(newsId)
  const expectedCodeIds = fromJS(codeIds || []).map(normalizeId).toSet()
  const expectedMemberships = expectedCodeIds.isEmpty() ? Set([null]) : expectedCodeIds
  const loadedMemberships = membershipsByNewsId(loadedSnapshot)

  return is(
    snapshotWithoutNews(savedSnapshot, [normalizedNewsId]),
    snapshotWithoutNews(loadedSnapshot, [normalizedNewsId])
  ) && is(loadedMemberships.get(normalizedNewsId, Set()), expectedMemberships)
}
